import { useState, useEffect, useCallback } from 'react';
import type { FC } from 'react';

import type {
  Vendor,
  Ply,
  BoxForm,
  PricingResult,
  ParseResponse,
  Step,
  FetchStatus,
} from './lib/types';
import {
  PLY_MATERIALS,
  COLOUR_OPTIONS,
  DEFAULT_PRINT_RATE,
  defaultPlies,
  formatINR,
} from './lib/constants';
import { callClaude } from './lib/api';
import { buildParsePrompt, buildPricingPrompt } from './lib/prompts';
import { s, globalCSS } from './lib/styles';

import StepIndicator from './components/StepIndicator';
import Page1_DataAndDescription from './components/Page1_DataAndDescription';
import Page2_BoxConfig from './components/Page2_BoxConfig';
import Page3_Results from './components/Page3_Results';
import ApiService from '../../services/apiService';
import URL from '../../urls/Urls';
import { useDispatch } from 'react-redux';
import { addVendorDetail } from '../../redux/oms/omsActions';

/**
 * BoxPricingPopup
 * ─────────────────────────────────────────────────────────────────────────────
 * Root component — owns all state and orchestrates the 3-page flow.
 *
 * Usage:
 *   import BoxPricingPopup from "./BoxPricingPopup/BoxPricingPopup";
 *   <BoxPricingPopup />
 *
 * Page 1 → user describes box → Claude parses → pre-fills Page 2
 * Page 2 → user reviews config (dimensions, vehicles, print, plies) → Claude prices → Page 3
 * Page 3 → sorted vendor quotes → user selects → Approve fires alert with vendor details
 * ─────────────────────────────────────────────────────────────────────────────
 */

interface IBoxPricingPopup {
  desc: string;
  location: string;
  quantity: string;
  category_id: number;
  tpm_id: number;
}

const BoxPricingPopup: FC<IBoxPricingPopup> = ({
  desc,
  location,
  quantity,
  category_id,
  tpm_id,
}) => {
  const dispatch = useDispatch();

  // ── Modal & navigation ─────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [step, setStep] = useState<Step>(1);

  // ── Vendor data ────────────────────────────────────────────────────────────
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorStatus, setVendorStatus] = useState<FetchStatus>('idle');
  const [vendorError, setVendorError] = useState<string | null>(null);

  // ── Page 1 ─────────────────────────────────────────────────────────────────
  const [description, setDescription] = useState<string>(desc);
  const [parsing, setParsing] = useState<boolean>(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseNotes, setParseNotes] = useState<string | null>(null);

  // ── Page 2 ─────────────────────────────────────────────────────────────────
  const [form, setForm] = useState<BoxForm>({
    length: '',
    width: '',
    height: '',
    quantity: '',
    location: '',
  });
  const [plies, setPlies] = useState<Ply[]>(defaultPlies());

  // Print config lives on Page 2
  const [printRatePerKg, setPrintRatePerKg] =
    useState<number>(DEFAULT_PRINT_RATE);
  const [colourOptionIndex, setColourOptionIndex] = useState<number>(0);

  // ── Page 3 ─────────────────────────────────────────────────────────────────
  const [pricing, setPricing] = useState<boolean>(false);
  const [result, setResult] = useState<PricingResult | null>(null);
  const [pricingErr, setPricingErr] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ── Fetch vendors on modal open ────────────────────────────────────────────
  const fetchVendors = useCallback(async (): Promise<void> => {
    setVendorStatus('loading');
    setVendorError(null);
    try {
      const { data } = await ApiService().client.get(
        URL.OMS.VENDOR_SUGGESTIONS
      );
      setVendors(data.vendors);
      setVendorStatus('success');
    } catch (e) {
      setVendorError((e as Error).message);
      setVendorStatus('error');
    }
  }, []);

  useEffect(() => {
    if (isOpen && vendorStatus === 'idle') fetchVendors();
  }, [isOpen, vendorStatus, fetchVendors]);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openModal = (): void => {
    setIsOpen(true);
    setStep(1);
    setResult(null);
    setParseNotes(null);
    setParseError(null);
    setSelectedIds(new Set());
  };

  const closeModal = (): void => setIsOpen(false);

  // ── Page 2 helpers ─────────────────────────────────────────────────────────
  const handleFormChange = (field: keyof BoxForm, val: string): void =>
    setForm((f) => ({ ...f, [field]: val }));

  const handleAddPly = (): void =>
    setPlies((p) => [
      ...p,
      { type: 'flute', material: 'Corrugating Medium', gsm: '', fluteType: '' },
      { type: 'liner', material: 'Kraft', gsm: '', fluteType: 'C-Flute' },
    ]);

  const handleRemovePly = (): void =>
    setPlies((p) => (p.length > 3 ? p.slice(0, -2) : p));

  const handleUpdatePly = (
    index: number,
    field: keyof Ply,
    val: string
  ): void =>
    setPlies((p) =>
      p.map((x, i) => (i === index ? { ...x, [field]: val } : x))
    );

  // ── Page 1 → 2: parse description with Claude ─────────────────────────────
  const handleParse = async (): Promise<void> => {
    if (!description.trim()) {
      setParseError('Please enter a box description.');
      return;
    }
    setParsing(true);
    setParseError(null);
    setParseNotes(null);

    try {
      const parsed = await callClaude<ParseResponse>(
        buildParsePrompt(description)
      );

      setForm({
        length: parsed.length != null ? String(parsed.length) : '',
        width: parsed.width != null ? String(parsed.width) : '',
        height: parsed.height != null ? String(parsed.height) : '',
        quantity,
        location,
      });

      setPlies(
        parsed.plies?.length >= 3
          ? parsed.plies.map((p) => ({
              type: p.type ?? 'liner',
              material: PLY_MATERIALS.includes(p.material ?? '')
                ? p.material!
                : 'Kraft',
              gsm: p.gsm != null ? String(p.gsm) : '',
              fluteType: p.fluteType ?? (p.type === 'liner' ? 'C-Flute' : ''),
            }))
          : defaultPlies()
      );

      if (parsed.parse_notes) setParseNotes(parsed.parse_notes);
      setStep(2);
    } catch {
      setParseError(
        'Could not parse description. Try adding dimensions, ply count and delivery location.'
      );
    } finally {
      setParsing(false);
    }
  };

  // ── Page 2 → 3: get quotes from Claude ────────────────────────────────────
  const handleGetQuotes = async (): Promise<void> => {
    const { length, width, height, location, quantity } = form;
    if (!length || !width || !height || !location || !quantity) {
      setPricingErr('Please fill in all dimension and delivery fields.');
      return;
    }
    if (!vendors.length) {
      setPricingErr('Vendor data not loaded. Please retry.');
      return;
    }

    setPricing(true);
    setResult(null);
    setPricingErr(null);
    setSelectedIds(new Set());

    const colourOption = COLOUR_OPTIONS[colourOptionIndex];
    try {
      const data = await callClaude<PricingResult>(
        buildPricingPrompt({
          form,
          plies,
          vendors,
          printConfig: {
            printRatePerKg,
            colourMultiplier: colourOption.multiplier,
            colourLabel: colourOption.label,
          },
        }),
        2000
      );
      setResult(data);
      setStep(3);
    } catch {
      setPricingErr(
        'Failed to calculate quotes. Check your inputs and try again.'
      );
    } finally {
      setPricing(false);
    }
  };

  // ── Page 3: vendor selection ───────────────────────────────────────────────
  const handleToggleVendor = (id: string): void =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── Page 3: approve selected vendors ──────────────────────────────────────
  const handleApprove = (): void => {
    if (!result || selectedIds.size === 0) {
      alert('Please select at least one vendor.');
      return;
    }

    const chosen = result.vendor_quotes.filter((q) => selectedIds.has(q.id));
    // const lines = chosen
    //   .map(
    //     (q) =>
    //       `Vendor ID   : ${q.id}\n` +
    //       `Vendor Name : ${q.name}\n` +
    //       `Price/unit  : ${formatINR(q.price_per_unit)}\n` +
    //       `Total Price : ${formatINR(q.total_price)}`
    //   )
    //   .join('\n\n──────────────────────\n\n');

    // alert(`✦ APPROVED VENDORS\n${'═'.repeat(30)}\n\n${lines}`);
    chosen.forEach((vendor) => {
      dispatch(
        addVendorDetail({
          sourcingId: category_id,
          tpm_id,
          vendor_id: Number(vendor.id),
          price: vendor.price_per_unit,
        })
      );
    });
    setIsOpen(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Syne:wght@700;800&display=swap"
        rel="stylesheet"
      />
      <style>{globalCSS}</style>

      {/* Trigger button */}
      <div>
        <button
          onClick={openModal}
          style={s.triggerBtn}
          className="xetgo-font-tag bold"
        >
          <span>📦</span> Get Box Price
        </button>
      </div>

      {/* Modal */}
      {isOpen && (
        <div
          style={s.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div style={s.modal}>
            {/* Header */}
            <div style={s.header}>
              <div>
                <span style={s.badge}>✦ AI Pricing Engine</span>
                <h2 style={s.title}>Box Quotation</h2>
              </div>
              <button onClick={closeModal} style={s.closeBtn}>
                ✕
              </button>
            </div>

            <StepIndicator step={step} />
            <div
              style={{ height: 1, background: '#f1f5f9', margin: '14px 0 0' }}
            />

            {/* Page router */}
            {step === 1 && (
              <Page1_DataAndDescription
                vendorStatus={vendorStatus}
                vendorError={vendorError}
                onRetryVendors={fetchVendors}
                vendorCount={vendors.length}
                description={description}
                onDescriptionChange={(v) => {
                  setDescription(v);
                  setParseError(null);
                }}
                parseError={parseError}
                parsing={parsing}
                onParse={handleParse}
              />
            )}

            {step === 2 && (
              <Page2_BoxConfig
                parseNotes={parseNotes}
                form={form}
                onFormChange={handleFormChange}
                plies={plies}
                onAddPly={handleAddPly}
                onRemovePly={handleRemovePly}
                onUpdatePly={handleUpdatePly}
                printRatePerKg={printRatePerKg}
                onPrintRateChange={setPrintRatePerKg}
                colourOptionIndex={colourOptionIndex}
                onColourChange={setColourOptionIndex}
                pricingError={pricingErr}
                pricing={pricing}
                onGetQuotes={handleGetQuotes}
                onBack={() => {
                  setStep(1);
                  setPricingErr(null);
                }}
              />
            )}

            {step === 3 && result && (
              <Page3_Results
                result={result}
                form={form}
                selectedIds={selectedIds}
                onToggle={handleToggleVendor}
                onApprove={handleApprove}
                onBack={() => setStep(2)}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BoxPricingPopup;
